import React, { memo } from 'react';
import { Button, Dropdown, message, Switch, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AssetReviewItemDto } from '@api/Api';
import { toast } from 'react-toastify';
import { ActionToast } from '@components/Toasts';
import { toggleShareAssetModal } from '@redux/actions/modalAction';
import {
  useDeleteAssetReviewLinkMutation,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import { ReactComponent as SettingsSvg } from '@assets/icons/dropdown/settings-linear.svg';
import { ReactComponent as RenameSvg } from '@assets/icons/dropdown/rename.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as ExternalLinkSvg } from '@assets/icons/external-link.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as UnlockSvg } from '@assets/icons/unlock.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { FolderWithItems } from '@pages/Campaigns/CampaignReview/utils';
import { downloadAll } from '@helpers/download';

import './Head.less';

type HeadProps = {
  review: AssetReviewItemDto;
  campaignId: string;
  selectedFolder: FolderWithItems | null;
  onGoBack: () => void;
};

export default memo(function Head({
  review,
  campaignId,
  selectedFolder,
  onGoBack
}: HeadProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const assetsDownloadUrls = selectedFolder?.items
    .filter((asset) => !!asset.asset?.permissions.download)
    .map((asset) => asset.asset?.versions[0].sourceUrl);
  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({});

  const { mutateAsync: deleteAssetReviewLink } =
    useDeleteAssetReviewLinkMutation({});

  const onOpenLinkSettings = (defaultActiveTab = 'sharing') => {
    dispatch(
      toggleShareAssetModal({
        visible: true,
        reviewId: review.id,
        defaultActiveTab
      })
    );
  };

  const onActiveChange = async (v: boolean) => {
    await updateAssetReview({
      reviewId: review.id,
      active: v
    });
  };

  const onRemoveLink = () => {
    let action = true;
    toast(
      <ActionToast
        title="Link removed"
        onUndo={() => {
          action = false;
        }}
        description={undefined}
        closeToast={undefined}
      />,
      {
        onClose: () => {
          if (action) {
            deleteAssetReviewLink({ reviewId: review.id });
            navigate(`/campaigns/single/${campaignId}?tab=sharedLinks`);
          }
        }
      }
    );
  };

  return (
    <div className="campaign-review-head">
      <div className="campaign-review-head-info">
        {!!selectedFolder && (
          <div className="campaign-review-head-title">
            <Button onClick={onGoBack} type="ghost">
              <ArrowLeftSvg />
            </Button>
            {selectedFolder.name}
            <Dropdown
              menu={{
                onClick: ({ domEvent }) => domEvent.stopPropagation(),
                items: [
                  {
                    key: 'download',
                    icon: <DownloadInvoiceSvg className="b-opacity-icon" />,
                    disabled: !assetsDownloadUrls?.length,
                    label: 'Download',
                    onClick: () => {
                      if (!assetsDownloadUrls?.length) return;
                      downloadAll(assetsDownloadUrls, selectedFolder.name);
                    }
                  }
                ]
              }}
              overlayClassName="asset_links_overlay"
              placement="bottomLeft"
              trigger={['click']}
            >
              <Button
                onClick={(e) => e.stopPropagation()}
                type="text"
                size="small"
                style={{ padding: 0, width: 24, height: 24 }}
              >
                <ActionSvg style={{ marginRight: 0 }} />
              </Button>
            </Dropdown>
          </div>
        )}

        {!selectedFolder && (
          <div className="campaign-review-head-title">
            {review.name}
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'renameLink',
                    label: 'Rename',
                    icon: <RenameSvg />,
                    onClick: () => onOpenLinkSettings()
                  },
                  {
                    type: 'divider'
                  },
                  {
                    key: 'copyLink',
                    label: 'Copy link',
                    icon: <LinkSvg />,
                    onClick: () => {
                      message.info('Copied to clipboard');
                      navigator.clipboard.writeText(review.url);
                    }
                  },
                  {
                    key: 'previewInNewTab',
                    label: 'Preview in new tab',
                    icon: <ExternalLinkSvg className="b-opacity-icon" />,
                    onClick: () => {
                      window.open(review.url, '_blank');
                    }
                  },
                  {
                    type: 'divider'
                  },
                  {
                    key: 'removeLink',
                    label: 'Delete',
                    icon: <DeleteSvg className="b-opacity-icon" />,
                    onClick: onRemoveLink
                  }
                ]
              }}
              overlayClassName="asset_links_overlay"
              placement="bottomLeft"
              trigger={['click']}
            >
              <Button
                onClick={(e) => e.stopPropagation()}
                type="text"
                size="small"
                style={{ padding: 0, width: 24, height: 24 }}
              >
                <ActionSvg style={{ marginRight: 0 }} />
              </Button>
            </Dropdown>
          </div>
        )}

        <div className="campaign-review-head-text">
          <Tooltip
            placement="bottomLeft"
            overlayClassName="toggle_favorite_overlay"
            title={review.password ? 'Password enabled' : 'Password disabled'}
          >
            {review.password ? <LockSvg /> : <UnlockSvg />}
          </Tooltip>
          {(review.allowComments || review.allowDownloads) && (
            <span>
              Allow: {review.allowComments && 'comments'}
              {review.allowDownloads && review.allowComments && ', '}
              {review.allowDownloads && 'downloads'}
            </span>
          )}
        </div>
      </div>
      <div className="campaign-review-head-actions">
        <div>
          <Switch
            loading={isUpdatingAssetReview}
            onChange={(v: boolean) => onActiveChange(v)}
            checked={review.active}
            style={{ marginRight: 12 }}
          />
          {review.active ? 'On' : 'Off'}
        </div>
        <Button
          type="ghost"
          icon={<SettingsSvg />}
          onClick={() => onOpenLinkSettings('settings')}
        >
          Link settings
        </Button>
      </div>
    </div>
  );
});
